import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Flame, Check, ChefHat, ShoppingBag, User, Phone, Clock, StickyNote, Trash2 } from "lucide-react";
import { endpoints, asset } from "../lib/api";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

const spiceDots = (s) => ({ Mild: 1, Medium: 2, Hot: 3 }[s] || 0);
const CAT_ORDER = ["Entrée", "Main Meals", "Biryani", "Curries", "Sides", "Desserts", "Beverages"];
const CAT_NOTE = {
  "Main Meals": "Served with your choice of chips & salad or mash & veg.",
  "Curries": "All curries are served with steamed rice.",
};

// Icon-led field used inside the dark pre-order panel.
// Defined OUTSIDE the parent component so it doesn't remount every render
// (otherwise it would drop focus + lose value on each keystroke).
const inputCls = "w-full bg-white/5 border border-white/15 rounded-xl pl-10 pr-3.5 py-2.5 text-white placeholder:text-white/55 text-sm outline-none focus:border-[#D4AF37] focus:bg-white/10 focus:ring-2 focus:ring-[#D4AF37]/25 transition";
function Field({ Icon, error, ...rest }) {
  return (
    <div>
      <div className="relative">
        <Icon size={15} className="absolute z-10 left-3.5 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" />
        <input {...rest} aria-invalid={!!error} className={`${inputCls} ${error ? "border-red-400/60 focus:border-red-400" : ""}`} />
      </div>
      {error && <p className="text-[11px] text-red-300 mt-1 pl-1" role="alert">{error}</p>}
    </div>
  );
}

// Individual dish row — Framer Motion mount-based reveal + gentle image parallax on hover.
// Uses `animate` (not whileInView) so every item renders immediately when the menu loads —
// nothing waits for a scroll observer, so the customer never sees blank/missing dishes.
function DishRow({ m, i, selected, onToggle }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      data-testid={`dish-${m.id}`}
      onClick={() => onToggle(m.name)}
      initial={reduce ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: Math.min(i * 0.04, 0.4), ease: [0.22, 1, 0.36, 1] }}
      whileHover={reduce ? undefined : { y: -3 }}
      className={`relative flex flex-wrap sm:flex-nowrap gap-x-4 gap-y-2 sm:gap-5 items-center group rounded-2xl p-3 -mx-3 cursor-pointer transition-[background-color,border-color,box-shadow] duration-300 border ${selected ? "border-[#D4AF37]/60 bg-[#D4AF37]/8 shadow-[0_8px_28px_-16px_rgba(212,175,55,0.55)]" : "border-transparent hover:border-[#D4AF37]/30 hover:bg-white/70 hover:shadow-[0_12px_30px_-16px_rgba(11,25,44,0.25)]"}`}
    >
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-2xl overflow-hidden soft-shadow">
        <motion.img
          src={asset(m.image)} alt={m.name} loading="lazy" decoding="async"
          initial={reduce ? false : { scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.9, delay: Math.min(i * 0.04, 0.4), ease: [0.22, 1, 0.36, 1] }}
          whileHover={reduce ? undefined : { scale: 1.12 }}
          className="w-full h-full object-cover"
          style={{ willChange: "transform" }}
        />
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0B192C]/45 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        {selected && (
          <span className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#D4AF37] text-[#0B192C] grid place-items-center shadow-md">
            <Check size={14} strokeWidth={3} />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 basis-0 sm:basis-auto border-b border-dashed border-[#0B192C]/15 group-hover:border-[#D4AF37]/50 pb-3 sm:pb-4 transition-colors duration-300">
        <div className="flex justify-between items-baseline gap-3">
          <h3 className="font-serif text-xl sm:text-2xl text-[#0B192C] flex flex-wrap items-center gap-2 transition-colors duration-300 group-hover:text-[#8a7020]">
            {m.name}
            {spiceDots(m.spice) > 0 && (
              <span className="flex gap-0.5">{[...Array(spiceDots(m.spice))].map((_, j) => <Flame key={j} size={13} className="text-[#D4AF37]" fill="#D4AF37" />)}</span>
            )}
          </h3>
          <span className="font-serif text-xl text-[#D4AF37] shrink-0" data-testid={`price-${m.id}`}>{m.price}</span>
        </div>
        <p className="text-sm text-[#222]/60 mt-1 font-light group-hover:text-[#222]/80 transition-colors duration-300">{m.description}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(m.name); }}
        data-testid={`add-${m.id}`}
        aria-pressed={selected}
        className={`shrink-0 basis-full sm:basis-auto w-full sm:w-auto text-sm px-4 py-2.5 sm:py-2 rounded-full border transition-all duration-300 ${selected
          ? "bg-[#0B192C] text-white border-[#0B192C]"
          : "border-[#0B192C]/25 text-[#0B192C] group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] group-hover:text-[#0B192C] group-hover:shadow-md"}`}
      >
        {selected ? <span className="inline-flex items-center justify-center gap-1.5"><Check size={16} /> <span className="sm:hidden">Added — tap to remove</span></span> : "Pre-order"}
      </button>
    </motion.div>
  );
}

export default function Dining() {
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({ guest_name: "", room_or_phone: "", when: "", notes: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => {
    if (errors[k]) setErrors((old) => ({ ...old, [k]: undefined }));
    setForm({ ...form, [k]: e.target.value });
  };
  useEffect(() => { endpoints.menu().then(setMenu).catch(() => {}); }, []);

  const toggle = (name) => {
    setSent(false);
    setCart((c) => c.includes(name) ? c.filter((x) => x !== name) : [...c, name]);
  };
  const removeAll = () => setCart([]);

  const priceOf = (name) => {
    const item = menu.find((m) => m.name === name);
    const match = item && item.price ? String(item.price).match(/[\d.]+/) : null;
    return match ? parseFloat(match[0]) : 0;
  };
  const total = cart.reduce((sum, n) => sum + priceOf(n), 0);
  const totalLabel = `$${total.toFixed(2)}`;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.guest_name.trim()) errs.guest_name = "Please enter your name.";
    if (!form.room_or_phone.trim()) errs.room_or_phone = "Please enter your room number or phone.";
    if (cart.length === 0) { toast.error("Add at least one dish from the menu."); return; }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setBusy(true);
    try {
      const r = await endpoints.preOrder({ ...form, items: cart, total: totalLabel });
      setSent(true); setCart([]);
      toast.success(r.message || "Pre-order received.");
    } catch { toast.error("Could not submit. Please call 02 6036 2077."); }
    finally { setBusy(false); }
  };

  const present = [...new Set(menu.map((m) => m.category))];
  const cats = CAT_ORDER.filter((c) => present.includes(c)).concat(present.filter((c) => !CAT_ORDER.includes(c)));

  return (
    <div>
      <PageHero overline="On-Site Restaurant" title="Our Restaurant"
        subtitle="Hearty à la carte classics and authentic Indian specialities — dine in or pre-order for your arrival."
        image="/static/generated/food-spread.png" />

      <section className="bg-[#F7F5F0] py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-12 lg:gap-14">
          {/* MENU */}
          <div className="lg:col-span-2 space-y-16">
            {cats.map((cat) => (
              <div key={cat} data-testid={`menu-cat-${cat}`}>
                <Reveal>
                  <h2 className="font-serif text-4xl text-[#0B192C] mb-2 flex items-center gap-4">{cat}<span className="flex-1 gold-line" /></h2>
                  {CAT_NOTE[cat] && <p className="text-xs text-[#0B192C]/50 italic mb-6">{CAT_NOTE[cat]}</p>}
                </Reveal>
                <div className="space-y-4">
                  {menu.filter((m) => m.category === cat).map((m, i) => (
                    <DishRow key={m.id} m={m} i={i} selected={cart.includes(m.name)} onToggle={toggle} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* PRE-ORDER PANEL — sticky, follows page scroll, no internal scrolling */}
          <aside
            id="preorder-panel"
            className="lg:sticky lg:top-28 h-fit"
            aria-label="Pre-order panel"
          >
            <div className="relative bg-gradient-to-b from-[#0B192C] to-[#0f2237] text-white rounded-3xl border border-[#D4AF37]/25 soft-shadow overflow-hidden" data-testid="preorder-panel">
              <div className="absolute -top-16 -right-12 w-48 h-48 rounded-full bg-[#D4AF37]/12 blur-3xl pointer-events-none" />

              {/* Header */}
              <div className="relative px-5 pt-5 pb-3.5 border-b border-white/10 flex items-start justify-between gap-3">
                <div>
                  <p className="overline text-[#D4AF37]">Pre-order</p>
                  <h3 className="font-serif text-[22px] leading-tight mt-0.5">Your meal</h3>
                  <p className="text-white/60 text-xs mt-0.5">We'll have it ready for your arrival.</p>
                </div>
                <div className="shrink-0 flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1.5 text-xs">
                  <ShoppingBag size={13} className="text-[#D4AF37]" />
                  <span className="font-medium tabular-nums" data-testid="preorder-count">{cart.length}</span>
                </div>
              </div>

              {/* Body */}
              <div className="relative px-5 py-4">
                {sent ? (
                  <motion.div
                    key="sent"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="text-center py-6" data-testid="preorder-success"
                  >
                    <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 180 }}
                      className="w-14 h-14 rounded-full bg-[#D4AF37] grid place-items-center mx-auto mb-3">
                      <Check className="text-[#0B192C]" size={22} strokeWidth={3} />
                    </motion.div>
                    <p className="font-serif text-2xl">Pre-order received</p>
                    <p className="text-white/65 text-sm mt-1">Thank you — your meal has been sent to our kitchen team.</p>
                    <p className="text-white/45 text-xs mt-3">Want to add more? Tap "Pre-order" on any dish and submit again.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="space-y-3.5">
                    {/* Items list */}
                    <div className="space-y-2" data-testid="preorder-items">
                      {cart.length === 0 ? (
                        <div className="flex items-center gap-3 rounded-xl bg-white/5 border border-dashed border-white/20 p-3.5">
                          <ChefHat size={18} className="text-[#D4AF37] shrink-0" />
                          <p className="text-white/70 text-sm leading-snug">Select dishes from the menu to start your order.</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-white/50 pb-1">
                            <span>Selected dishes</span>
                            <button type="button" onClick={removeAll} className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors" data-testid="preorder-clear">
                              <Trash2 size={11} /> Clear
                            </button>
                          </div>
                          <ul className="space-y-1.5">
                            <AnimatePresence initial={false}>
                              {cart.map((c) => (
                                <motion.li
                                  key={c}
                                  layout
                                  initial={{ opacity: 0, x: 12 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -12, transition: { duration: 0.18 } }}
                                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                                  className="group flex items-center justify-between gap-3 rounded-lg bg-white/8 hover:bg-white/15 border border-white/10 px-3 py-2 text-sm transition-colors"
                                >
                                  <button type="button" onClick={() => toggle(c)} className="flex-1 text-left flex items-center gap-2 min-w-0" title="Remove">
                                    <span className="w-5 h-5 rounded-full bg-[#D4AF37]/25 text-[#D4AF37] grid place-items-center shrink-0 group-hover:bg-[#D4AF37] group-hover:text-[#0B192C] transition-colors text-[11px] font-bold">×</span>
                                    <span className="truncate text-white/95">{c}</span>
                                  </button>
                                  <span className="text-white font-serif tabular-nums shrink-0">${priceOf(c).toFixed(2)}</span>
                                </motion.li>
                              ))}
                            </AnimatePresence>
                          </ul>
                          <div className="flex items-center justify-between pt-2.5 mt-1 border-t border-white/15">
                            <span className="uppercase tracking-[0.22em] text-[10px] text-[#D4AF37]">Estimated total</span>
                            <motion.span
                              key={totalLabel}
                              initial={{ scale: 0.92, opacity: 0.7 }} animate={{ scale: 1, opacity: 1 }}
                              transition={{ type: "spring", stiffness: 300, damping: 18 }}
                              className="font-serif text-[24px] text-[#D4AF37] tabular-nums" data-testid="preorder-total"
                            >{totalLabel}</motion.span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Guest details */}
                    <div className="space-y-2 pt-0.5">
                      <Field Icon={User} placeholder="Your name *" value={form.guest_name} onChange={set("guest_name")} error={errors.guest_name} data-testid="preorder-name" />
                      <Field Icon={Phone} placeholder="Room number or phone *" value={form.room_or_phone} onChange={set("room_or_phone")} error={errors.room_or_phone} data-testid="preorder-contact" />
                      <Field Icon={Clock} placeholder="Preferred time (e.g. 7:00 PM)" value={form.when} onChange={set("when")} data-testid="preorder-time" />
                      <div className="relative">
                        <StickyNote size={15} className="absolute z-10 left-3.5 top-3 text-white/60 pointer-events-none" />
                        <textarea rows={2} placeholder="Notes (allergies, spice level…)" value={form.notes} onChange={set("notes")} data-testid="preorder-notes"
                          className="w-full resize-none bg-white/5 border border-white/15 rounded-xl pl-10 pr-3.5 py-2.5 text-white placeholder:text-white/55 text-sm outline-none focus:border-[#D4AF37] focus:bg-white/10 focus:ring-2 focus:ring-[#D4AF37]/25 transition" />
                      </div>
                    </div>

                    {/* Submit */}
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-[#D4AF37] text-[#0B192C] py-3 rounded-xl font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      data-testid="preorder-submit"
                      disabled={cart.length === 0 || busy}
                    >
                      {busy ? "Sending…" : (
                        <>Submit Pre-order <span className="opacity-70">·</span> <span className="tabular-nums">{cart.length} item{cart.length === 1 ? "" : "s"}</span> <span className="opacity-70">·</span> <span className="tabular-nums">{totalLabel}</span></>
                      )}
                    </motion.button>
                    <p className="text-white/45 text-[11px] text-center">Prices are indicative; final total confirmed at the restaurant.</p>
                  </form>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile: persistent order pill — the panel sits below the whole menu on small
          screens, so without this the guest gets no feedback while selecting dishes. */}
      <AnimatePresence>
        {cart.length > 0 && (
          <motion.button
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            onClick={() => document.getElementById("preorder-panel")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="lg:hidden fixed bottom-5 left-4 z-[65] flex items-center gap-2 bg-[#0B192C] text-white border border-[#D4AF37]/40 rounded-full pl-4 pr-5 py-3 shadow-xl text-sm"
            data-testid="preorder-mobile-bar"
          >
            <ShoppingBag size={15} className="text-[#D4AF37]" />
            <span className="tabular-nums">{cart.length} item{cart.length === 1 ? "" : "s"} · {totalLabel}</span>
            <span className="text-[#D4AF37] font-medium">Review & submit</span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
